import type { EffectId, EffectSettings } from './audio/engine';
import type { VisSettings } from './ui/visualizer';

export type Mode = 'audio' | 'visual';

export interface Settings {
  presetId: string;
  octave: number;
  velocity: number;
  volume: number;
  fx: Record<EffectId, EffectSettings>;
  vis: VisSettings;
  lastMode: Mode;
  takeCounter: number;
}

export const DEFAULT_FX: Record<EffectId, EffectSettings> = {
  // A touch of room by default so the very first note sounds lovely.
  reverb: { mix: 0.22, a: 0.42, b: 0.55 },
  saturation: { mix: 0, a: 0.4, b: 0.7 },
  granular: { mix: 0, a: 0.35, b: 0.5 },
};

const DEFAULTS: Settings = {
  presetId: 'grand-piano',
  octave: 4,
  velocity: 100,
  volume: 0.8,
  fx: DEFAULT_FX,
  vis: { aspect: '9:16', style: 'halo', palette: 'notes', backdrop: 'paper', caption: '', tag: true, imageFit: 'fill' },
  lastMode: 'audio',
  takeCounter: 0,
};

const KEY = 'spiel:settings';

export function loadSettings(): Settings {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Settings>;
    return {
      ...DEFAULTS,
      ...raw,
      fx: {
        reverb: { ...DEFAULTS.fx.reverb, ...raw.fx?.reverb },
        saturation: { ...DEFAULTS.fx.saturation, ...raw.fx?.saturation },
        granular: { ...DEFAULTS.fx.granular, ...raw.fx?.granular },
      },
      vis: { ...DEFAULTS.vis, ...raw.vis },
    };
  } catch {
    return structuredClone(DEFAULTS);
  }
}

let timer = 0;
export function saveSettings(s: Settings): void {
  clearTimeout(timer);
  timer = window.setTimeout(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
    } catch {
      /* storage unavailable: settings just won't persist */
    }
  }, 200);
}

export function storeGet(key: string): string | null {
  try {
    return localStorage.getItem(`spiel:${key}`);
  } catch {
    return null;
  }
}

export function storeSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(`spiel:${key}`);
    else localStorage.setItem(`spiel:${key}`, value);
  } catch {
    /* ignore */
  }
}
